/* ⚠️ GENERATED from app/services/reactions.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   😍 reactions.ts — more than one way to like a post.

   A REACTION IS A LIKE WITH A KIND. It is counted in likesCount like any like,
   lives on the like's own document (posts/{id}/likes/{uid}.r — likeRoutes.js),
   and the post carries:
     reactionCounts  { care: n, haha: n, … }   every kind but the plain like
     reactionsBy     { uid: 'haha' }           the same people the like ledger
                                               names (first 500), kinds ≠ like
   A plain like is "whatever is left": likesCount − the other kinds. So every
   like made before reactions existed is still exactly a like, and nothing old
   has to be rewritten. Server-owned (MERGED_RULES), shared by both clients.
   ═══════════════════════════════════════════════════════════════════════════ */

export const REACTIONS = ['like', 'care', 'haha', 'wow', 'sad', 'angry'];
export const REACTION_EMOJI = {
  like: '❤️',
  care: '🥰',
  haha: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😡'
};
export const REACTION_COLOR = {
  like: '#FF6B6B',
  care: '#F7B125',
  haha: '#F7B125',
  wow: '#F7B125',
  sad: '#F7B125',
  angry: '#E9710F'
};
export function asReaction(v) {
  return REACTIONS.indexOf(v) >= 0 ? v : 'like';
}

/** My reaction on a post — from the post's own map, else a plain like when I like it. */
export function myReaction(post, uid, liked) {
  if (!liked || !uid) return null;
  const by = (post ? post.reactionsBy : null) || {};
  return asReaction(by[String(uid)]);
}

/** The total likes on a post (the counter, or the ledger for an old post). */
function likeTotal(post) {
  const n = Number(post && post.likesCount);
  const a = Array.isArray(post && post.likes) ? post.likes.length : 0;
  return Math.max(Number.isFinite(n) ? n : 0, a);
}

/** Every kind with its count, biggest first; a plain like is what the others leave. */
export function reactionTotals(post) {
  const c = (post ? post.reactionCounts : null) || {};
  let others = 0;
  const out = [];
  for (const r of REACTIONS) {
    if (r === 'like') continue;
    const n = Math.max(0, Number(c[r]) || 0);
    others += n;
    if (n > 0) out.push({
      r,
      n
    });
  }
  const likes = Math.max(0, likeTotal(post) - others);
  if (likes > 0) out.push({
    r: 'like',
    n: likes
  });
  return out.sort((a, b) => b.n - a.n || REACTIONS.indexOf(a.r) - REACTIONS.indexOf(b.r));
}

/** The three kinds a card shows beside the count. */
export function topReactions(post, k = 3) {
  return reactionTotals(post).slice(0, k).map(x => x.r);
}

/**
 * The post as it will be after `uid` goes from `prev` (null = no like) to `next`
 * (null = unlike) — for the card to change at once. Only the reaction fields;
 * the like count and ledger are the like code's.
 */
export function applyReaction(post, uid, prev, next) {
  const counts = Object.assign({}, (post ? post.reactionCounts : null) || {});
  const by = Object.assign({}, (post ? post.reactionsBy : null) || {});
  if (prev && prev !== 'like') counts[prev] = Math.max(0, (Number(counts[prev]) || 0) - 1);
  if (next && next !== 'like') counts[next] = (Number(counts[next]) || 0) + 1;
  if (next && next !== 'like') by[uid] = next;else delete by[uid];
  return {
    reactionCounts: counts,
    reactionsBy: by
  };
}

/* ══ 😍 THE FACEBOOK-STYLE PARTS ════════════════════════════════════════════
   Everything below is shared by the phone and the web so the tray, the summary
   and the "who reacted" list behave the same on both. */

/** English names (the web; the phone uses its own locale keys `rx_<kind>`). */
export const REACTION_LABEL = {
  like: 'Like',
  care: 'Care',
  haha: 'Haha',
  wow: 'Wow',
  sad: 'Sad',
  angry: 'Angry'
};

/**
 * The summary on the RIGHT of a post: the kinds people picked (most first, at
 * most three) and how many reacted in all — me included, the way the list it
 * opens counts. Nothing to show → total 0.
 */
export function reactionSummary(post, k = 3) {
  const all = reactionTotals(post);
  return {
    tops: all.slice(0, k).map(x => x.r),
    total: likeTotal(post)
  };
}

/** The tabs of the "who reacted" list: All, then every kind that has anyone. */
export function reactionTabs(post) {
  const all = reactionTotals(post);
  const tabs = [{
    r: null,
    n: likeTotal(post)
  }];
  return tabs.concat(all.map(x => ({
    r: x.r,
    n: x.n
  })));
}

/**
 * Which reaction a finger is over while it slides across the open tray.
 *
 * The tray is `count` equal slots between `left` and `left + width` (window
 * coordinates). A finger above or below the tray by more than `slop` is not
 * on it (→ -1) — that is how "release to cancel" is reached: slide off and
 * let go. Sideways past either end still picks the end slot while the finger
 * is level with the tray, the way Facebook's does, so the last face is easy
 * to land on.
 */
export function trayHit(x, y, box, count = REACTIONS.length, slop = 56) {
  if (!box || !(box.width > 0) || !(count > 0)) return -1;
  if (y < box.top - slop || y > box.top + box.height + slop) return -1;
  if (x < box.left - slop || x > box.left + box.width + slop) return -1;
  const i = Math.floor((x - box.left) / box.width * count);
  return Math.max(0, Math.min(count - 1, i));
}

/**
 * 🟢 MY PRESS, SHOWN BEFORE THE SERVER HAS COUNTED IT.
 *
 * A pending press remembers the counters it was made against. While the post
 * in hand still carries exactly those counters the server has not caught up,
 * so the press is applied on top; the moment either counter moves, the copy
 * in hand is newer than the press and is shown as it is. (A press nobody
 * confirms is dropped by its owner after a few seconds.)
 */

export function pendingFor(post, uid, prev, next) {
  return {
    baseCount: likeTotal(post),
    baseCounts: Object.assign({}, (post ? post.reactionCounts : null) || {}),
    prev,
    next,
    uid: String(uid)
  };
}
function sameCounts(a, b) {
  const ka = Object.keys(a || {}).filter(k => Number(a[k]) !== 0);
  const kb = Object.keys(b || {}).filter(k => Number(b[k]) !== 0);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (Number(a[k]) !== Number((b || {})[k])) return false;
  return true;
}
export function withPending(post, pend) {
  if (!post || !pend) return post;
  if (likeTotal(post) !== pend.baseCount || !sameCounts(post.reactionCounts, pend.baseCounts)) return post;
  const delta = (pend.next ? 1 : 0) - (pend.prev ? 1 : 0);
  const rx = applyReaction(post, pend.uid, pend.prev, pend.next);
  const out = Object.assign({}, post, rx, {
    likesCount: Math.max(0, pend.baseCount + delta)
  });
  /* the ledger moves with the count, or likeTotal would still read the old array */
  if (Array.isArray(post.likes) && delta !== 0) {
    const arr = post.likes.filter(u => String(u) !== pend.uid);
    out.likes = delta > 0 ? arr.concat([pend.uid]) : arr;
  }
  return out;
}
